import { Injectable, inject } from '@angular/core';

import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { NOT_APPLICABLE, TrackData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { AlertEvent, AlertViewModel, Source } from './model';

@Injectable({ providedIn: 'root' })
export class AlertTrackingService {
    private readonly trackingService = inject(TrackingService);

    trackLoadAlerts(events: AlertEvent[], source?: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'live alerts icon',
            ...this.getBetInfoTrackingData(events, source),
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, trackingData);
    }

    trackUserInteractionAlerts(events: AlertEvent[], isSelected: boolean, source?: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_ACTION_EVENT]: isSelected ? 'select' : 'unselect',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'live alerts icon',
            ...this.getBetInfoTrackingData(events, source),
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    trackOverlayAlerts(events: AlertEvent[], isMultiMode: boolean, source: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `choose ${!isMultiMode ? 'alerts' : 'events'} overlay`,
            ...this.getBetInfoTrackingData(events, source),
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, trackingData);
    }

    trackCloseOverlayAlerts(events: AlertEvent[], isListView: boolean, isMultiMode: boolean, source: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'close',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `choose ${isMultiMode && isListView ? 'events' : 'alerts'} overlay`,
            ...this.getBetInfoTrackingData(events, source),
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    trackSubmitOverlayAlerts(events: AlertEvent[], alerts: { [key: string]: AlertViewModel[] }, isSingleMode: boolean, source: string): void {
        let trackingData: TrackData = [];
        events.forEach((event) => {
            const marketIds = event.marketIds?.filter(isDefined) ?? [];
            const eventAlert = alerts[event.eventId];
            const selectedOptions = (eventAlert && eventAlert.filter((item) => item.isSelected).map((option) => option.info.name)) || [];

            if (selectedOptions.length > 0 || Object.keys(alerts).length === 0) {
                const betTrackingData = {
                    [trackingConstants.COMPONENT_ACTION_EVENT]: 'submit',
                    [trackingConstants.COMPONENT_LOCATION_EVENT]: `${this.getSource(source)} - ${isSingleMode ? 'choose' : 'live'} alerts overlay`,
                    [trackingConstants.COMPONENT_EVENT_DETAILS]: `alerts-${this.getUniqueData(selectedOptions, true)}`,
                    [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'sports alerts',
                    [trackingConstants.COMPONENT_LABEL_EVENT]: 'event alerts',
                    [trackingConstants.COMPONENT_POSITION_EVENT]: event.betType?.toLocaleLowerCase(),
                    [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
                    [trackingConstants.MARKET_ID]: this.getUniqueData(marketIds),
                    [trackingConstants.SPORT_LEAGUE_ID]: event.leagueId.toString(),
                    [trackingConstants.SPORT_ID]: event.sportId.toString(),
                    [trackingConstants.SPORT_EVENT_ID]: event.eventId.toString(),
                    [trackingConstants.BETSLIP_ID]: event.betNumber,
                };
                trackingData.push(betTrackingData);
            }
        });

        if (isSingleMode) {
            this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData[0]);
        } else {
            this.trackingService.track(trackingConstants.EVENT_ALERTS_SUBMISSION, trackingData);
        }
    }

    trackBackOverlayAlerts(events: AlertEvent[], source: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'choose alerts overlay - back',
            ...this.getBetInfoTrackingData(events, source),
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    trackAlertsSuccess(events: AlertEvent[], subscribed: boolean, source?: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `live alerts ${subscribed ? 'success' : 'off'}`,
            ...this.getBetInfoTrackingData(events, source),
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, trackingData);
    }

    private getBetInfoTrackingData(events: AlertEvent[], source?: string): TrackData {
        const marketIds = events.flatMap((event) => event.marketIds).filter(isDefined);
        const leagueIds = events.map((event) => event.leagueId);
        const sportIds = events.map((event) => event.sportId);
        const sportEventIds = events.map((event) => event.eventId);

        return {
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getSource(source),
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'sports alerts',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'event alerts',
            [trackingConstants.COMPONENT_POSITION_EVENT]: events[0].betType?.toLocaleLowerCase(),
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.MARKET_ID]: this.getUniqueData(marketIds),
            [trackingConstants.SPORT_LEAGUE_ID]: this.getUniqueData(leagueIds),
            [trackingConstants.SPORT_ID]: this.getUniqueData(sportIds),
            [trackingConstants.SPORT_EVENT_ID]: this.getUniqueData(sportEventIds),
            [trackingConstants.BETSLIP_ID]: events[0].betNumber,
        };
    }

    private getUniqueData(data: (number | string)[], useColon?: boolean): string {
        const separator = useColon ? ':' : ',';
        const trackingData = data.filter((x) => x !== undefined);
        return Array.from(new Set(trackingData)).join(separator);
    }

    private getSource(source?: string): string {
        let betLocation: string;

        switch (source) {
            case Source.Mybets: {
                betLocation = 'my bets';
                break;
            }
            case Source.Betslip: {
                betLocation = 'bet receipt';
                break;
            }
            case Source.Event || Source.Scoreboard || Source.Mybet: {
                betLocation = source;
                break;
            }
            default: {
                betLocation = '';
                break;
            }
        }
        return betLocation;
    }
}
